import "./styles.css";
import { extractInvoice } from "./extract.js";

const app = document.querySelector("#app");
const formatter = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });
const dateFormatter = new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "short", year: "numeric" });

const state = {
  invoices: [],
  summary: { totalOpenCents: 0, overdueCount: 0, dueSoonCount: 0, paidCount: 0 },
  loading: true,
  filter: "all",
  search: "",
  selectedFile: null,
  config: { b2Configured: false, authRequired: false },
  accessToken: sessionStorage.getItem("fatture_access_token") || "",
};

const icons = {
  upload: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V4m0 0L7 9m5-5 5 5M5 15v4h14v-4"/></svg>`,
  invoice: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h8l4 4v14H7zM15 3v5h4M10 12h6m-6 4h6"/></svg>`,
  alert: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 9v4m0 4h.01M4.7 19h14.6L12 5z"/></svg>`,
  clock: `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>`,
  check: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>`,
  search: `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m16 16 4 4"/></svg>`,
  more: `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg>`,
  close: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>`,
  file: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2h8l4 4v16H6zM14 2v5h4"/></svg>`,
};

function authHeaders(extra = {}) {
  return state.accessToken ? { ...extra, Authorization: `Bearer ${state.accessToken}` } : extra;
}

async function api(path, options = {}) {
  const response = await fetch(path, { ...options, headers: authHeaders(options.headers || {}) });
  if (response.status === 401) {
    state.config.authRequired = true;
    renderAccessGate();
    throw new Error("Autenticazione richiesta");
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || "Si è verificato un errore.");
  return body;
}

function formatDate(value) {
  if (!value) return "—";
  const parsed = new Date(`${value}T12:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? value : dateFormatter.format(parsed);
}

function statusMeta(invoice) {
  const status = invoice.status;
  if (status === "paid") return { label: "Pagata", className: "paid" };
  if (status === "overdue") return { label: invoice.daysDelta === -1 ? "Scaduta ieri" : `Scaduta da ${Math.abs(invoice.daysDelta)} gg`, className: "overdue" };
  if (status === "due_soon") return { label: invoice.daysDelta === 0 ? "Scade oggi" : `Scade tra ${invoice.daysDelta} gg`, className: "due-soon" };
  return { label: "In attesa", className: "pending" };
}

function escapeHtml(value = "") {
  return String(value).replace(/[&<>'"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[char]);
}

function renderShell() {
  app.innerHTML = `
    <div class="app-shell">
      <aside class="sidebar">
        <div class="brand"><span class="brand-mark">H</span><span>HISI</span></div>
        <nav aria-label="Navigazione principale">
          <button class="nav-item active" aria-label="Fatture">${icons.invoice}<span>Fatture</span><span class="nav-count" id="nav-count">0</span></button>
          <button class="nav-item disabled" aria-label="Avvisi email — disponibile prossimamente" title="Disponibile nella prossima fase">${icons.alert}<span>Avvisi email</span><span class="soon">Presto</span></button>
        </nav>
        <div class="sidebar-bottom">
          <div class="automation-status"><span class="pulse"></span><div><strong>Controllo attivo</strong><small>Ogni giorno · 08:00</small></div></div>
          <div class="org-card"><span class="org-avatar">HI</span><div><strong>HISI</strong><small>Amministrazione</small></div></div>
        </div>
      </aside>
      <main>
        <header class="topbar">
          <div><p class="eyebrow">Amministrazione</p><h1>Scadenziario fatture</h1></div>
          <button class="primary" id="open-upload" aria-label="Carica fattura">${icons.upload}<span>Carica fattura</span></button>
        </header>
        <section id="banner-area"></section>
        <section class="summary-grid" id="summary-grid"></section>
        <section class="workspace-card">
          <div class="table-toolbar">
            <div class="filter-tabs" role="tablist">
              <button class="filter-tab active" data-filter="all">Tutte</button>
              <button class="filter-tab" data-filter="overdue">Scadute</button>
              <button class="filter-tab" data-filter="due_soon">In scadenza</button>
              <button class="filter-tab" data-filter="paid">Pagate</button>
            </div>
            <label class="search-box">${icons.search}<input id="search" type="search" placeholder="Cerca fattura o fornitore" /></label>
          </div>
          <div id="table-area"></div>
        </section>
      </main>
    </div>
    <div id="modal-root"></div>
    <div id="toast-root" aria-live="polite"></div>
  `;
  document.querySelector("#open-upload").addEventListener("click", openUpload);
  document.querySelectorAll(".filter-tab").forEach((button) => button.addEventListener("click", () => {
    state.filter = button.dataset.filter;
    document.querySelectorAll(".filter-tab").forEach((item) => item.classList.toggle("active", item === button));
    renderTable();
  }));
  document.querySelector("#search").addEventListener("input", (event) => {
    state.search = event.target.value.toLowerCase();
    renderTable();
  });
}

function renderSummary() {
  const { totalOpenCents, overdueCount, dueSoonCount, paidCount } = state.summary;
  document.querySelector("#summary-grid").innerHTML = `
    <article class="summary-card total"><span class="metric-icon">${icons.invoice}</span><div><p>Da incassare</p><strong>${formatter.format(totalOpenCents / 100)}</strong><small>Totale fatture aperte</small></div></article>
    <article class="summary-card overdue"><span class="metric-icon">${icons.alert}</span><div><p>Scadute</p><strong>${overdueCount}</strong><small>${overdueCount === 1 ? "Fattura da gestire" : "Fatture da gestire"}</small></div></article>
    <article class="summary-card soon-card"><span class="metric-icon">${icons.clock}</span><div><p>Entro 7 giorni</p><strong>${dueSoonCount}</strong><small>In avvicinamento</small></div></article>
    <article class="summary-card paid-card"><span class="metric-icon">${icons.check}</span><div><p>Pagate</p><strong>${paidCount}</strong><small>Archiviate</small></div></article>
  `;
  document.querySelector("#nav-count").textContent = state.invoices.length;
}

function renderBanner() {
  const area = document.querySelector("#banner-area");
  if (!state.config.b2Configured) {
    area.innerHTML = `<div class="setup-banner"><span>${icons.alert}</span><div><strong>Archivio Backblaze da collegare</strong><p>L’app è pronta. Inserisci endpoint, bucket e credenziali B2 per abilitare il caricamento.</p></div><button id="setup-info">Cosa serve</button></div>`;
    document.querySelector("#setup-info").addEventListener("click", () => showToast("Servono: nome bucket, endpoint S3, Key ID e Application Key.", "info"));
  } else {
    area.innerHTML = "";
  }
}

function renderTable() {
  const area = document.querySelector("#table-area");
  if (state.loading) {
    area.innerHTML = `<div class="loading-list">${[1,2,3,4].map(() => '<div class="skeleton-row"></div>').join("")}</div>`;
    return;
  }
  const filtered = state.invoices.filter((invoice) => {
    const matchesFilter = state.filter === "all" || invoice.status === state.filter;
    const haystack = `${invoice.invoiceNumber} ${invoice.supplierName} ${invoice.fileName}`.toLowerCase();
    return matchesFilter && haystack.includes(state.search);
  });
  if (!filtered.length) {
    area.innerHTML = `<div class="empty-state"><span>${icons.invoice}</span><h3>${state.invoices.length ? "Nessun risultato" : "Il tuo scadenziario è vuoto"}</h3><p>${state.invoices.length ? "Prova a cambiare filtro o ricerca." : "Carica la prima fattura: estrarremo scadenza e importo."}</p>${state.invoices.length ? "" : '<button class="secondary" id="empty-upload">Carica una fattura</button>'}</div>`;
    document.querySelector("#empty-upload")?.addEventListener("click", openUpload);
    return;
  }
  area.innerHTML = `
    <div class="invoice-table" role="table">
      <div class="table-head" role="row"><span>Fattura</span><span>Fornitore</span><span>Scadenza</span><span>Importo</span><span>Stato</span><span></span></div>
      ${filtered.map((invoice) => {
        const status = statusMeta(invoice);
        return `<div class="table-row" role="row" data-id="${invoice.id}">
          <div class="invoice-cell"><span class="file-tile">${icons.file}</span><div><strong>${escapeHtml(invoice.invoiceNumber || "Senza numero")}</strong><small>${escapeHtml(invoice.fileName)}</small></div></div>
          <span class="supplier">${escapeHtml(invoice.supplierName || "Non rilevato")}</span>
          <span>${formatDate(invoice.dueDate)}</span>
          <strong class="amount">${formatter.format(invoice.amountCents / 100)}</strong>
          <span><span class="status-chip ${status.className}">${status.label}</span></span>
          <button class="icon-button row-menu" aria-label="Azioni">${icons.more}</button>
        </div>`;
      }).join("")}
    </div>`;
  document.querySelectorAll(".table-row").forEach((row) => row.addEventListener("click", (event) => {
    if (event.target.closest(".row-menu")) event.preventDefault();
    openDetail(row.dataset.id);
  }));
}

function renderAll() {
  renderSummary();
  renderBanner();
  renderTable();
}

function showToast(message, type = "success") {
  const root = document.querySelector("#toast-root");
  const toast = document.createElement("div");
  toast.className = `toast ${type}`;
  toast.innerHTML = `<span>${type === "success" ? icons.check : icons.alert}</span><p>${escapeHtml(message)}</p>`;
  root.append(toast);
  window.setTimeout(() => toast.remove(), 4200);
}

function closeModal() {
  document.querySelector("#modal-root").innerHTML = "";
  state.selectedFile = null;
}

function modalFrame(content, className = "") {
  const root = document.querySelector("#modal-root");
  root.innerHTML = `<div class="modal-backdrop"><div class="modal ${className}" role="dialog" aria-modal="true">${content}</div></div>`;
  root.querySelector(".modal-backdrop").addEventListener("click", (event) => { if (event.target === event.currentTarget) closeModal(); });
  root.querySelector(".close-modal")?.addEventListener("click", closeModal);
}

function openUpload() {
  modalFrame(`
    <div class="modal-header"><div><p class="eyebrow">Nuovo documento</p><h2>Carica una fattura</h2><p>PDF o XML, massimo 15 MB.</p></div><button class="icon-button close-modal">${icons.close}</button></div>
    <div class="upload-body">
      <label class="dropzone" id="dropzone"><input id="file-input" type="file" accept=".pdf,.xml,application/pdf,application/xml,text/xml" /><span class="upload-circle">${icons.upload}</span><h3>Trascina qui la fattura</h3><p>oppure <u>scegli un file</u> dal computer</p><small>Il file verrà archiviato su Backblaze B2</small></label>
      <div class="upload-progress hidden" id="upload-progress"><span class="file-tile large">${icons.file}</span><div><strong id="progress-name"></strong><p id="progress-message">Lettura del documento…</p><span class="progress-track"><i></i></span></div></div>
      <div class="parse-error hidden" id="parse-error"></div>
    </div>
  `, "upload-modal");
  const input = document.querySelector("#file-input");
  const dropzone = document.querySelector("#dropzone");
  input.addEventListener("change", () => input.files[0] && prepareFile(input.files[0]));
  ["dragenter", "dragover"].forEach((name) => dropzone.addEventListener(name, (event) => { event.preventDefault(); dropzone.classList.add("dragging"); }));
  ["dragleave", "drop"].forEach((name) => dropzone.addEventListener(name, (event) => { event.preventDefault(); dropzone.classList.remove("dragging"); }));
  dropzone.addEventListener("drop", (event) => event.dataTransfer.files[0] && prepareFile(event.dataTransfer.files[0]));
}

async function prepareFile(file) {
  if (file.size > 15 * 1024 * 1024) return showToast("Il file supera il limite di 15 MB.", "error");
  state.selectedFile = file;
  document.querySelector("#dropzone").classList.add("hidden");
  const progress = document.querySelector("#upload-progress");
  progress.classList.remove("hidden");
  document.querySelector("#progress-name").textContent = file.name;
  try {
    const data = await extractInvoice(file);
    window.setTimeout(() => renderReview(data), 350);
  } catch (error) {
    const fallback = {
      invoiceNumber: file.name.replace(/\.[^.]+$/, ""), supplierName: "", issueDate: "", dueDate: "",
      amountCents: null, currency: "EUR", extractionConfidence: 0, parseSource: "manual",
    };
    showToast(error.message, "error");
    window.setTimeout(() => renderReview(fallback), 250);
  }
}

function renderReview(data) {
  const root = document.querySelector(".modal");
  root.innerHTML = `
    <div class="modal-header"><div><p class="eyebrow">Verifica dati</p><h2>Controlla prima di salvare</h2><p>I campi estratti restano modificabili.</p></div><button class="icon-button close-modal">${icons.close}</button></div>
    <form id="invoice-form" class="review-form">
      <div class="detected-file"><span class="file-tile">${icons.file}</span><div><strong>${escapeHtml(state.selectedFile.name)}</strong><small>${(state.selectedFile.size / 1024).toFixed(0)} KB · Rilevamento ${data.extractionConfidence}%</small></div><span class="confidence ${data.extractionConfidence >= 67 ? "good" : "low"}">${data.extractionConfidence >= 67 ? "Dati rilevati" : "Da verificare"}</span></div>
      <div class="field-grid">
        <label><span>Numero fattura</span><input name="invoiceNumber" required value="${escapeHtml(data.invoiceNumber)}" /></label>
        <label><span>Fornitore</span><input name="supplierName" placeholder="Ragione sociale" value="${escapeHtml(data.supplierName)}" /></label>
        <label><span>Data documento</span><input name="issueDate" type="date" value="${data.issueDate || ""}" /></label>
        <label><span>Data scadenza</span><input name="dueDate" type="date" required value="${data.dueDate || ""}" /></label>
        <label class="amount-field"><span>Importo</span><div><span>€</span><input name="amount" inputmode="decimal" required value="${data.amountCents === null ? "" : (data.amountCents / 100).toFixed(2).replace(".", ",")}" /></div></label>
      </div>
      <div class="modal-actions"><button type="button" class="text-button close-modal">Annulla</button><button class="primary" type="submit">Salva fattura</button></div>
    </form>`;
  root.querySelectorAll(".close-modal").forEach((button) => button.addEventListener("click", closeModal));
  root.querySelector("#invoice-form").addEventListener("submit", submitInvoice);
}

async function submitInvoice(event) {
  event.preventDefault();
  const button = event.submitter;
  button.disabled = true;
  button.textContent = "Salvataggio…";
  try {
    const values = Object.fromEntries(new FormData(event.currentTarget));
    const amount = Number(values.amount.replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) throw new Error("Inserisci un importo valido.");
    const form = new FormData();
    form.append("file", state.selectedFile);
    form.append("metadata", JSON.stringify({
      invoiceNumber: values.invoiceNumber, supplierName: values.supplierName, issueDate: values.issueDate,
      dueDate: values.dueDate, amountCents: Math.round(amount * 100), currency: "EUR",
    }));
    await api("/api/invoices", { method: "POST", body: form });
    closeModal();
    showToast("Fattura salvata e inserita nello scadenziario.");
    await loadData();
  } catch (error) {
    showToast(error.message, "error");
    button.disabled = false;
    button.textContent = "Salva fattura";
  }
}

function openDetail(id) {
  const invoice = state.invoices.find((item) => item.id === id);
  if (!invoice) return;
  const status = statusMeta(invoice);
  modalFrame(`
    <div class="modal-header"><div><p class="eyebrow">Dettaglio fattura</p><h2>${escapeHtml(invoice.invoiceNumber)}</h2><p>${escapeHtml(invoice.supplierName || "Fornitore non rilevato")}</p></div><button class="icon-button close-modal">${icons.close}</button></div>
    <div class="detail-body">
      <div class="detail-amount"><small>Importo documento</small><strong>${formatter.format(invoice.amountCents / 100)}</strong><span class="status-chip ${status.className}">${status.label}</span></div>
      <dl class="detail-grid"><div><dt>Data documento</dt><dd>${formatDate(invoice.issueDate)}</dd></div><div><dt>Scadenza</dt><dd>${formatDate(invoice.dueDate)}</dd></div><div><dt>File originale</dt><dd>${escapeHtml(invoice.fileName)}</dd></div><div><dt>Caricata il</dt><dd>${formatDate(invoice.createdAt?.slice(0, 10))}</dd></div></dl>
      <div class="detail-actions"><button class="secondary" id="open-document">Apri documento</button>${invoice.status === "paid" ? "" : '<button class="primary" id="mark-paid">Segna come pagata</button>'}</div>
    </div>
  `, "detail-modal");
  document.querySelector("#open-document")?.addEventListener("click", async (event) => {
    event.currentTarget.disabled = true;
    try {
      const response = await fetch(`/api/invoices/${id}/file`, { headers: authHeaders() });
      if (!response.ok) throw new Error("Impossibile aprire il documento.");
      const objectUrl = URL.createObjectURL(await response.blob());
      window.open(objectUrl, "_blank", "noopener");
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) { showToast(error.message, "error"); }
    finally { event.currentTarget.disabled = false; }
  });
  document.querySelector("#mark-paid")?.addEventListener("click", async (event) => {
    event.currentTarget.disabled = true;
    try {
      await api(`/api/invoices/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: "paid" }) });
      closeModal(); showToast("Fattura segnata come pagata."); await loadData();
    } catch (error) { showToast(error.message, "error"); event.currentTarget.disabled = false; }
  });
}

function renderAccessGate() {
  app.innerHTML = `<main class="access-page"><div class="access-card"><div class="brand centered"><span class="brand-mark">H</span><span>HISI</span></div><p class="eyebrow">Area riservata</p><h1>Scadenziario fatture</h1><p>Inserisci il token di accesso dell’organizzazione.</p><form id="access-form"><label><span>Token di accesso</span><input type="password" name="token" autocomplete="current-password" required /></label><button class="primary">Accedi</button></form><small>Il token resta solo in questa sessione del browser.</small></div></main>`;
  document.querySelector("#access-form").addEventListener("submit", async (event) => {
    event.preventDefault();
    state.accessToken = new FormData(event.currentTarget).get("token");
    sessionStorage.setItem("fatture_access_token", state.accessToken);
    renderShell();
    try { await loadData(); } catch { sessionStorage.removeItem("fatture_access_token"); }
  });
}

async function loadData() {
  state.loading = true;
  renderTable();
  const [config, payload] = await Promise.all([api("/api/config"), api("/api/invoices")]);
  state.config = config;
  state.invoices = payload.invoices;
  state.summary = payload.summary;
  state.loading = false;
  renderAll();
}

renderShell();
loadData().catch((error) => {
  state.loading = false;
  if (error.message !== "Autenticazione richiesta") {
    renderTable();
    showToast(error.message, "error");
  }
});
